(function (root, factory) {
  var api = factory(root);

  root.PjPhotoQuality = api;

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  /*
   * Red / yellow / green photo-quality flags under each pet's upload card.
   *
   * After a photo uploads, CORS cuts the pet's face out in the background
   * (design-pipeline/photo-check) and scores the cutout 0-100. This module
   * polls for that score, bands it against marketing-owned thresholds, and
   * shows the flag:
   *
   * - red: blocks Preview Your Design and Add to Cart until the photo is
   *   replaced. An ambiguous photo (two competing pets) is red too, and so is
   *   a reject from the upload print-PPI gate (flagPpiReject).
   * - yellow: non-blocking; the order is stamped for artist review.
   * - green: only as a recovery, after the same slot was red or yellow. A
   *   first-try pass shows nothing.
   *
   * Add to Cart also waits for every check still running: a photo is never
   * bought before it is verified (INT-1787679144). Anything short of a score
   * (error, timeout, check disabled) still fails open: no flag and no block.
   * Never lose a sale to a scoring outage.
   */

  var CONFIG_ID = 'pjPhotoQualityConfig';
  var FLAG_CHANGE_EVENT = 'pj-design-lab:photo-flag-change';
  var SESSION_STORAGE_KEY = 'pjDesignLabSession';
  var PREVIEW_PATH = '/attachments/preview/';
  var LONG_POLL_SECONDS = 25;

  var DEFAULTS = {
    enabled: false,
    // Green shows only as a recovery after red or yellow; a first-try pass
    // stays clean. Set false to show green on every passing photo.
    greenOnRecoveryOnly: true,
    // Placeholders from the flags wireframe until Gate A signs off real cutoffs.
    redBelow: 40,
    greenFrom: 75,
    // How long the upload loader waits for a score before showing the photo.
    loaderHoldMs: 8000,
    // How long Add to Cart waits for a check still running before it turns
    // the customer away to try again.
    cartWaitMs: 10000,
    // After this the check is treated as failed (fail open).
    pollTimeoutMs: 180000,
    redTitle: 'Please upload a new photo to continue',
    redBody:
      "We can't seem to get the cropping right on this one — a clear, well-lit shot of your pet works best.",
    redCta: 'Upload a New Photo',
    redPreviewHint: 'Upload a new photo to preview your design.',
    redCartToast: 'Please upload a new photo to continue',
    redLabToast:
      "We can't get the cropping right on this photo — please upload a new one",
    yellowTitle: "We'll take a closer look",
    yellowBody:
      'This photo may be tricky to print. Our artists will review it and may reach out for a different photo.',
    yellowLink: 'Upload a different photo',
    greenTitle: 'Great photo!',
    greenBody: 'Clear, well-lit, and ready to print on your pajamas.',
    checkingText: 'Checking your photo…',
    cartWaitingText: 'Finishing your photo check…',
    pendingCartToast: "We're still checking your photo — please try again in a moment",
  };

  var COPY_KEYS = [
    'redTitle',
    'redBody',
    'redCta',
    'redPreviewHint',
    'redCartToast',
    'redLabToast',
    'yellowTitle',
    'yellowBody',
    'yellowLink',
    'greenTitle',
    'greenBody',
    'checkingText',
    'cartWaitingText',
    'pendingCartToast',
  ];

  // Placeholder names until the Flag Analytics Events story fixes them.
  var EVENTS = {
    shown: 'pj_photo_flag_shown',
    cleanPass: 'pj_photo_check_clean_pass',
    blocked: 'pj_photo_flag_blocked',
    replaced: 'pj_photo_flag_replaced',
    resolved: 'pj_photo_flag_resolved',
    proceeded: 'pj_photo_flag_proceeded',
  };

  var ICONS = {
    green:
      '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>',
    yellow:
      '<svg viewBox="0 0 24 24"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
    red:
      '<svg viewBox="0 0 24 24"><path d="M7.86 2h8.28L22 7.86v8.28L16.14 22H7.86L2 16.14V7.86L7.86 2z"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
  };

  // ---------------------------------------------------------------- config

  function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function parseJson(raw) {
    if (raw == null || raw === '') return null;
    if (isObject(raw)) return raw;
    if (typeof raw !== 'string') return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  function score(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) && number >= 0 && number <= 100 ? number : fallback;
  }

  function duration(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) && number >= 0 ? number : fallback;
  }

  function text(value, fallback) {
    if (value == null) return fallback;
    var trimmed = String(value).trim();
    return trimmed || fallback;
  }

  function parseConfig(raw) {
    var parsed = parseJson(raw);
    var source = isObject(parsed) ? parsed : {};
    var redBelow = score(source.redBelow, DEFAULTS.redBelow);
    var greenFrom = score(source.greenFrom, DEFAULTS.greenFrom);
    // A green cutoff under the red one would make yellow impossible and let a
    // red score read as green; fall back to the defaults rather than guess.
    if (greenFrom < redBelow) {
      redBelow = DEFAULTS.redBelow;
      greenFrom = DEFAULTS.greenFrom;
    }

    var config = {
      enabled: source.enabled === true || source.enabled === 'true',
      greenOnRecoveryOnly:
        source.greenOnRecoveryOnly !== false && source.greenOnRecoveryOnly !== 'false',
      redBelow: redBelow,
      greenFrom: greenFrom,
      loaderHoldMs: duration(source.loaderHoldMs, DEFAULTS.loaderHoldMs),
      cartWaitMs: duration(source.cartWaitMs, DEFAULTS.cartWaitMs),
      pollTimeoutMs: duration(source.pollTimeoutMs, DEFAULTS.pollTimeoutMs),
    };
    COPY_KEYS.forEach(function (key) {
      config[key] = text(source[key], DEFAULTS[key]);
    });
    return config;
  }

  function readConfig(doc) {
    var documentRef = doc || root.document;
    var el =
      documentRef && typeof documentRef.getElementById === 'function'
        ? documentRef.getElementById(CONFIG_ID)
        : null;
    var config = parseConfig(el ? el.textContent : null);
    config.checkUrl = el ? el.getAttribute('data-check-url') || '' : '';
    config.apiToken = el ? el.getAttribute('data-api-token') || '' : '';
    config.active = Boolean(el && el.getAttribute('data-active') === 'true');
    return config;
  }

  function isEnabled(config) {
    var resolved = config || readConfig();
    return Boolean(
      resolved.enabled && resolved.active && resolved.checkUrl && resolved.apiToken
    );
  }

  // ---------------------------------------------------------------- banding

  /**
   * The band a finished check puts the photo in, or null when there is
   * nothing to act on (fail open).
   */
  function bandFor(check, config) {
    var resolved = config || DEFAULTS;
    if (!check || !check.settled) return null;
    if (check.outcome === 'ambiguous') return 'red';
    if (check.outcome !== 'scored') return null;
    var value = Number(check.confidenceScore);
    if (check.confidenceScore == null || !Number.isFinite(value)) return null;
    if (value < resolved.redBelow) return 'red';
    if (value < resolved.greenFrom) return 'yellow';
    return 'green';
  }

  /**
   * Which flag a band shows. Green is a recovery confirmation: it shows only
   * on the first pass after this slot was red or yellow, unless the config
   * turns recovery-only off to show green on every passing photo.
   */
  function flagFor(band, recovering, config) {
    if (band === 'red' || band === 'yellow') return band;
    if (band !== 'green') return null;
    var recoveryOnly = Boolean(config && config.greenOnRecoveryOnly);
    return !recoveryOnly || recovering ? 'green' : null;
  }

  /** The source ref CORS addresses the upload by (matches PjDesignLabPipeline.sourceRef). */
  function sourceRef(url) {
    var raw = String(url == null ? '' : url).trim();
    var index = raw.indexOf(PREVIEW_PATH);
    if (index === -1) return raw;
    return raw
      .slice(index + PREVIEW_PATH.length)
      .split('?')[0]
      .split('#')[0];
  }

  // ---------------------------------------------------------------- client

  function abortError() {
    var error = new Error('aborted');
    error.name = 'AbortError';
    return error;
  }

  /**
   * Starts a check and long-polls it until it settles. Resolves to the final
   * check, or null on any failure or timeout - the caller fails open on null.
   */
  function createClient(options) {
    var settings = options || {};
    var fetchImpl = settings.fetch || root.fetch;
    var now =
      settings.now ||
      function () {
        return Date.now();
      };

    function request(url, init) {
      return fetchImpl(url, init).then(function (response) {
        if (!response || !response.ok) throw new Error('photo check request failed');
        return response.json();
      });
    }

    function headers(json) {
      var value = { 'api-token': settings.token };
      if (json) value['Content-Type'] = 'application/json';
      return value;
    }

    function check(imageUrl, checkOptions) {
      var opts = checkOptions || {};
      var signal = opts.signal;
      var deadline = now() + duration(opts.timeoutMs, DEFAULTS.pollTimeoutMs);
      if (!settings.endpoint || !settings.token || typeof fetchImpl !== 'function') {
        return Promise.resolve(null);
      }

      function poll(view) {
        if (signal && signal.aborted) return Promise.reject(abortError());
        if (!view || typeof view !== 'object') return null;
        if (view.settled) return view;
        if (!view.id || now() >= deadline) return null;
        return request(
          settings.endpoint + '/' + encodeURIComponent(view.id) + '?wait=' + LONG_POLL_SECONDS,
          { method: 'GET', headers: headers(false), signal: signal }
        ).then(poll);
      }

      return request(settings.endpoint, {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify({
          sourceImageRef: sourceRef(imageUrl),
          sessionId: opts.sessionId || undefined,
        }),
        signal: signal,
      })
        .then(poll)
        .catch(function (error) {
          if (error && error.name === 'AbortError') throw error;
          return null;
        });
    }

    return { check: check };
  }

  // ---------------------------------------------------------------- runtime

  var slots = {};

  function slotState(number) {
    var key = String(number);
    if (!slots[key]) {
      slots[key] = {
        generation: 0,
        pending: false,
        band: null,
        flag: null,
        score: null,
        checkId: null,
        everFlagged: false,
        // Red or yellow since the slot's last pass: the next pass is a
        // recovery and earns the green flag, once.
        recovering: false,
        startedAt: 0,
        controller: null,
        promise: null,
        previous: null,
        // 'ppi' while the red flag comes from a print-PPI reject: the photo was
        // never kept, so the flag stands under an empty upload card.
        source: null,
        // The print-PPI gate's own title and body for that red flag.
        copy: null,
        // The flag a print-PPI reject left before the next upload began, so
        // that upload's result can count as a recovery.
        recoveringFrom: null,
      };
    }
    return slots[key];
  }

  function snapshot(state) {
    return {
      pending: state.pending,
      band: state.band,
      flag: state.flag,
      score: state.score,
      checkId: state.checkId,
    };
  }

  function track(name, detail) {
    try {
      root.dataLayer = root.dataLayer || [];
      root.dataLayer.push(Object.assign({ event: name }, detail || {}));
    } catch (error) {
      // Analytics must never break the upload card.
    }
  }

  function sessionId() {
    var storage = null;
    var value = null;
    try {
      storage = root.sessionStorage || null;
      value = storage ? storage.getItem(SESSION_STORAGE_KEY) : null;
    } catch (error) {
      storage = null;
    }
    if (value) return value;
    var cryptoApi = root.crypto;
    value =
      cryptoApi && typeof cryptoApi.randomUUID === 'function'
        ? cryptoApi.randomUUID()
        : 'dl-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    try {
      // Shared with the Design Lab so its per-session generation cap counts
      // the photo checks too.
      if (storage) storage.setItem(SESSION_STORAGE_KEY, value);
    } catch (error) {
      storage = null;
    }
    return value;
  }

  function slotElement(number) {
    var doc = root.document;
    return doc && typeof doc.querySelector === 'function'
      ? doc.querySelector('.photo-upload-slot-' + number)
      : null;
  }

  function cardFor(slot) {
    return slot ? slot.closest('.uploaderSection') || slot.parentElement : null;
  }

  function isSlotActive(slot) {
    if (!slot) return false;
    var card = slot.closest('.uploaderSection');
    if (!card) return true;
    if (card.style && card.style.display === 'none') return false;
    return Boolean(card.querySelector('input:not([disabled])'));
  }

  function activeSlotNumbers(options) {
    var doc = root.document;
    if (!doc || typeof doc.querySelectorAll !== 'function') return [];
    var withPhoto = Boolean(options && options.withPhoto);
    var numbers = [];
    Array.prototype.forEach.call(doc.querySelectorAll('.photo-upload-slot'), function (slot) {
      var match = /photo-upload-slot-(\d+)/.exec(slot.className || '');
      if (!match || !isSlotActive(slot)) return;
      // A slot redrawn empty (combined-listing swap, cleared upload) no longer
      // carries the photo its old result was about. A slot mid-upload still
      // submits its previous photo, so it keeps that photo's result.
      var status = slot.dataset ? slot.dataset.uploadStatus : '';
      var number = Number(match[1]);
      if (
        withPhoto &&
        status !== 'done' &&
        status !== 'loading' &&
        !isShownPpiReject(number)
      ) {
        return;
      }
      numbers.push(number);
    });
    return numbers;
  }

  // A print-PPI reject blocks only while its flag is on screen: a slot redrawn
  // by a combined-listing swap has no flag left to explain the block.
  function isShownPpiReject(number) {
    var state = slotState(number);
    if (state.source !== 'ppi' || state.flag !== 'red') return false;
    var doc = root.document;
    var el = doc && typeof doc.getElementById === 'function'
      ? doc.getElementById('pjPhotoFlag-' + number)
      : null;
    return Boolean(el && !el.hidden);
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function flagHtml(flag, config, number, copy) {
    var title = (copy && copy.title) || config[flag + 'Title'];
    var body = (copy && copy.body) || config[flag + 'Body'];
    var action = '';
    if (flag === 'red') {
      action =
        '<button class="photo-flag-btn" type="button" data-photo-flag-replace="' +
        number +
        '">' +
        escapeHtml(config.redCta) +
        '</button>';
    } else if (flag === 'yellow') {
      action =
        '<button class="photo-flag-link" type="button" data-photo-flag-replace="' +
        number +
        '">' +
        escapeHtml(config.yellowLink) +
        '</button>';
    }
    return (
      '<span class="photo-flag-ico" aria-hidden="true">' +
      ICONS[flag] +
      '</span>' +
      '<span class="photo-flag-text"><strong>' +
      escapeHtml(title) +
      '</strong><span>' +
      escapeHtml(body) +
      '</span>' +
      action +
      '</span>'
    );
  }

  function ensureFlagElement(slot, number) {
    var doc = root.document;
    var id = 'pjPhotoFlag-' + number;
    var el = doc.getElementById(id);
    if (el && el.previousElementSibling !== slot && slot.insertAdjacentElement) {
      slot.insertAdjacentElement('afterend', el);
    }
    if (el) return el;
    el = doc.createElement('div');
    el.id = id;
    el.className = 'photo-flag';
    el.hidden = true;
    // A sibling of the slot, not a child: the uploaders rewrite the slot's
    // innerHTML on every state change.
    if (slot.insertAdjacentElement) slot.insertAdjacentElement('afterend', el);
    el.addEventListener('click', function (event) {
      var target = event.target && event.target.closest
        ? event.target.closest('[data-photo-flag-replace]')
        : null;
      if (!target) return;
      event.preventDefault();
      var input = slotElement(number) && slotElement(number).querySelector('.fileInput');
      if (input) input.click();
    });
    return el;
  }

  function setDescribedBy(slot, id, on) {
    Array.prototype.forEach.call(
      slot.querySelectorAll('.fileInput, .photo-replace-btn'),
      function (control) {
        var ids = (control.getAttribute('aria-describedby') || '')
          .split(/\s+/)
          .filter(function (value) {
            return value && value !== id;
          });
        if (on) ids.push(id);
        if (ids.length) control.setAttribute('aria-describedby', ids.join(' '));
        else control.removeAttribute('aria-describedby');
      }
    );
  }

  function stampProperty(card, number, name, value) {
    var doc = root.document;
    var id = 'pjPhotoProperty-' + name + '-' + number;
    var input = doc.getElementById(id);
    if (!value) {
      if (input && input.parentNode) input.parentNode.removeChild(input);
      return;
    }
    if (!input) {
      input = doc.createElement('input');
      input.type = 'hidden';
      input.id = id;
      input.name = 'properties[_' + name + '_' + number + ']';
      card.appendChild(input);
    }
    input.value = String(value);
  }

  /**
   * Line-item properties so artist review can find the order: the band and
   * score per pet, or `pending` plus the check id when Add to Cart ran before
   * the score landed. Red never reaches the cart.
   */
  function stamp(number) {
    var slot = slotElement(number);
    var card = cardFor(slot);
    if (!card || !root.document) return;
    var state = slotState(number);
    var stampable = state.pending || state.band === 'yellow' || state.band === 'green';
    var flagValue = state.pending ? 'pending' : stampable ? state.band : '';
    stampProperty(card, number, 'photo_flag', flagValue);
    stampProperty(
      card,
      number,
      'photo_confidence',
      stampable && state.score != null ? state.score : ''
    );
    stampProperty(card, number, 'photo_check', stampable ? state.checkId : '');
  }

  /** The print-PPI warning outranks a green pass, so the green flag stays hidden. */
  function ppiWarningHides(number, flag) {
    var slot = slotElement(number);
    return (
      flag === 'green' &&
      Boolean(slot && slot.querySelector('[data-print-ppi-message="warn"]'))
    );
  }

  /** Redraws the slot's flag. Returns true when that already announced a change. */
  function render(number) {
    var slot = slotElement(number);
    if (!slot || !root.document) return false;
    var config = readConfig();
    // Flags off (metafield or product toggle): leave the card exactly as the
    // uploader drew it, and never add flag markup.
    if (!isEnabled(config) && !root.document.getElementById('pjPhotoFlag-' + number)) {
      return false;
    }
    var state = slotState(number);
    var hasPhoto = slot.dataset.uploadStatus === 'done';
    var flagEl = ensureFlagElement(slot, number);
    var zone = slot.querySelector('.photo-upload-zone');
    var previousFlag = slot.dataset.photoFlag || '';

    ['red', 'yellow', 'green'].forEach(function (band) {
      if (zone) zone.classList.remove('photo-zone--' + band);
    });

    var ppiReject = state.source === 'ppi' && state.flag === 'red';
    var flag = hasPhoto || ppiReject ? state.flag : null;

    // When the print-PPI gate warned about this photo too, the card shows only
    // the worse of the two: a red or yellow flag hides the PPI warning, and
    // the PPI warning outranks a green pass.
    var ppiWarning = slot.querySelector('[data-print-ppi-message="warn"]');
    if (ppiWarning) {
      var flagWins = flag === 'red' || flag === 'yellow';
      ppiWarning.hidden = flagWins;
      slot.classList.toggle('has-print-ppi-warning', !flagWins);
      if (zone) zone.classList.toggle('has-warning', !flagWins);
    }
    if (ppiWarningHides(number, flag)) flag = null;

    slot.dataset.photoFlag = flag || (hasPhoto && state.pending ? 'pending' : '');

    if (flag) {
      flagEl.className = 'photo-flag photo-flag--' + flag;
      flagEl.setAttribute('role', flag === 'red' ? 'alert' : 'status');
      flagEl.innerHTML = flagHtml(flag, config, number, ppiReject ? state.copy : null);
      flagEl.hidden = false;
      if (zone) zone.classList.add('photo-zone--' + flag);
    } else if (hasPhoto && state.pending) {
      flagEl.className = 'photo-flag photo-flag--checking';
      flagEl.setAttribute('role', 'status');
      flagEl.textContent = config.checkingText;
      flagEl.hidden = false;
    } else {
      flagEl.hidden = true;
      flagEl.removeAttribute('role');
      flagEl.textContent = '';
    }
    setDescribedBy(slot, flagEl.id, !flagEl.hidden);
    stamp(number);
    // The uploaders announce the redrawn slot before they call render(), so a
    // score that landed during the loader hold reaches Preview Your Design and
    // Add to Cart only through this.
    if (slot.dataset.photoFlag === previousFlag) return false;
    notify(number);
    return true;
  }

  function renderAndNotify(number) {
    if (!render(number)) notify(number);
  }

  function notify(number) {
    var state = slotState(number);
    var doc = root.document;
    if (!doc || typeof doc.dispatchEvent !== 'function' || typeof root.CustomEvent !== 'function') {
      return;
    }
    doc.dispatchEvent(
      new root.CustomEvent(FLAG_CHANGE_EVENT, {
        detail: { slot: number, band: state.band, flag: state.flag, pending: state.pending },
      })
    );
  }

  function toast(message) {
    var doc = root.document;
    var atc = doc && doc.querySelector ? doc.querySelector('atc-submit') : null;
    if (atc && typeof atc.showNotification === 'function') {
      atc.showNotification(message, 'warning');
    }
  }

  function scrollToSlot(number) {
    var card = cardFor(slotElement(number));
    if (card && typeof card.scrollIntoView === 'function') {
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // A red photo cannot ride into the customizer: send the customer back to
  // the flag on the product page to fix it.
  function leaveLabIfOpen(number, config) {
    var doc = root.document;
    var modal = doc && doc.getElementById ? doc.getElementById('pvModal') : null;
    if (!modal || !modal.classList.contains('open')) return;
    if (typeof root.closePv === 'function') root.closePv();
    toast(config.redLabToast);
    scrollToSlot(number);
  }

  function settle(number, generation, check) {
    var state = slotState(number);
    if (state.generation !== generation) return;
    var config = readConfig();
    var wasFlagged = state.everFlagged;
    var wasRecovering = state.recovering;
    var previousFlag = (state.previous && state.previous.flag) || state.recoveringFrom;
    var fromPpi = Boolean(state.recoveringFrom);
    state.recoveringFrom = null;

    state.pending = false;
    state.controller = null;
    state.previous = null;
    state.band = bandFor(check, config);
    state.score = check && check.confidenceScore != null ? Number(check.confidenceScore) : null;
    state.checkId = check && check.id ? check.id : null;
    state.flag = flagFor(state.band, wasRecovering, config);
    if (state.band === 'red' || state.band === 'yellow') {
      state.everFlagged = true;
      state.recovering = true;
    }
    if (state.band === 'green') state.recovering = false;

    // Out of the Design Lab before anyone hears about the red flag: the
    // preview launcher reprocesses the open Lab on every flag change.
    if (state.flag === 'red') leaveLabIfOpen(number, config);
    renderAndNotify(number);

    var detail = { slot: number, band: state.band, score: state.score };
    if (state.flag && !ppiWarningHides(number, state.flag)) track(EVENTS.shown, detail);
    // A pass on the slot's first photo is still a clean pass for analytics,
    // whether or not its green flag is shown.
    if (state.band === 'green' && !wasFlagged) track(EVENTS.cleanPass, detail);
    // A print-PPI reject is resolved by any photo that clears the gate; the
    // score only has to stay out of red (a failed check still counts).
    if (previousFlag === 'red' && state.band !== 'red' && (state.band || fromPpi)) {
      track(EVENTS.resolved, Object.assign({}, detail, fromPpi ? { source: 'ppi' } : {}));
    }
  }

  /**
   * Starts the check for a photo that just uploaded. Returns a promise of the
   * band (null when there is none); the uploader waits on it briefly through
   * waitBriefly(), never for the whole check.
   */
  function start(number, imageUrl) {
    var config = readConfig();
    var state = slotState(number);
    if (state.controller) state.controller.abort();

    state.previous = snapshot(state);
    if (state.flag === 'red' || state.flag === 'yellow') {
      track(EVENTS.replaced, { slot: number, from: state.flag });
    }
    state.generation += 1;
    state.band = null;
    state.flag = null;
    state.score = null;
    state.checkId = null;
    state.source = null;
    state.copy = null;
    state.startedAt = Date.now();

    if (!isEnabled(config) || !imageUrl) {
      state.pending = false;
      state.previous = null;
      state.promise = Promise.resolve(null);
      return state.promise;
    }

    var generation = state.generation;
    var controller = typeof root.AbortController === 'function' ? new root.AbortController() : null;
    state.controller = controller;
    state.pending = true;

    var client = createClient({ endpoint: config.checkUrl, token: config.apiToken });
    state.promise = client
      .check(imageUrl, {
        sessionId: sessionId(),
        timeoutMs: config.pollTimeoutMs,
        signal: controller ? controller.signal : undefined,
      })
      .then(
        function (check) {
          settle(number, generation, check);
          return slotState(number).generation === generation ? slotState(number).band : null;
        },
        function () {
          // Aborted by a newer upload, a cancel, or a cleared slot.
          return null;
        }
      );
    return state.promise;
  }

  /**
   * Resolves true once the check settles or the loader hold runs out
   * (counted from when the check started), and false if the upload was
   * cancelled or superseded meanwhile - the uploader must not commit then.
   */
  function waitBriefly(number) {
    var state = slotState(number);
    var generation = state.generation;
    var config = readConfig();
    var remaining = Math.max(0, config.loaderHoldMs - (Date.now() - state.startedAt));
    var current = function () {
      return slotState(number).generation === generation;
    };
    if (!state.pending || !remaining) return Promise.resolve(current());
    return new Promise(function (resolve) {
      var timer = setTimeout(function () {
        resolve(current());
      }, remaining);
      Promise.resolve(state.promise).then(function () {
        clearTimeout(timer);
        resolve(current());
      });
    });
  }

  /** The slot lost its photo (PPI reject, failed upload with nothing saved). */
  function clear(number) {
    var state = slotState(number);
    if (state.controller) state.controller.abort();
    state.generation += 1;
    state.controller = null;
    state.pending = false;
    state.band = null;
    state.flag = null;
    state.score = null;
    state.checkId = null;
    state.previous = null;
    state.source = null;
    state.copy = null;
    renderAndNotify(number);
  }

  /**
   * A print-PPI reject (the photo is too small to print) gets the red flag:
   * same card, same blocks, with the PPI gate's own title and body. Returns
   * false while the flags are off, so the uploader keeps the gate's plain
   * message instead.
   */
  function flagPpiReject(number, copy) {
    var config = readConfig();
    if (!isEnabled(config)) return false;
    var state = slotState(number);
    if (state.controller) state.controller.abort();
    state.generation += 1;
    state.controller = null;
    state.pending = false;
    state.previous = null;
    state.recoveringFrom = null;
    state.band = 'red';
    state.flag = 'red';
    state.score = null;
    state.checkId = null;
    state.source = 'ppi';
    state.copy = copy && (copy.title || copy.body) ? copy : null;
    state.everFlagged = true;
    state.recovering = true;

    leaveLabIfOpen(number, config);
    renderAndNotify(number);
    track(EVENTS.shown, { slot: number, band: 'red', score: null, source: 'ppi' });
    return true;
  }

  /**
   * A new photo cleared the print-PPI gate on a slot it had rejected: take the
   * reject's flag down while the photo uploads. Its check then records the
   * recovery.
   */
  function releasePpiReject(number) {
    var state = slotState(number);
    if (state.source !== 'ppi') return;
    track(EVENTS.replaced, { slot: number, from: 'red', source: 'ppi' });
    state.flag = null;
    state.band = null;
    state.source = null;
    state.copy = null;
    state.recoveringFrom = 'red';
    renderAndNotify(number);
  }

  /**
   * The customer cancelled an upload while its check was starting: put back
   * the previous photo's result, which the uploader is restoring.
   */
  function cancel(number) {
    var state = slotState(number);
    if (!state.previous) return;
    if (state.controller) state.controller.abort();
    var previous = state.previous;
    state.generation += 1;
    state.controller = null;
    state.previous = null;
    state.pending = false;
    state.band = previous.band;
    state.flag = previous.flag;
    state.score = previous.score;
    state.checkId = previous.checkId;
    state.source = null;
    state.copy = null;
    renderAndNotify(number);
  }

  function isRed(number) {
    return slotState(number).flag === 'red';
  }

  function firstRedSlot() {
    var numbers = activeSlotNumbers({ withPhoto: true });
    for (var index = 0; index < numbers.length; index += 1) {
      if (isRed(numbers[index])) return numbers[index];
    }
    return null;
  }

  function firstPendingSlot() {
    var numbers = activeSlotNumbers({ withPhoto: true });
    for (var index = 0; index < numbers.length; index += 1) {
      if (slotState(numbers[index]).pending) return numbers[index];
    }
    return null;
  }

  function hasPending() {
    return activeSlotNumbers().some(function (number) {
      return slotState(number).pending;
    });
  }

  /** Waits for the active slots' checks, up to the cart wait. */
  function settleAll(timeoutMs) {
    var wait = duration(timeoutMs, readConfig().cartWaitMs);
    var pending = activeSlotNumbers()
      .map(function (number) {
        return slotState(number);
      })
      .filter(function (state) {
        return state.pending;
      })
      .map(function (state) {
        return state.promise;
      });
    if (!pending.length) return Promise.resolve();
    return new Promise(function (resolve) {
      var timer = setTimeout(resolve, wait);
      Promise.all(pending).then(function () {
        clearTimeout(timer);
        resolve();
      });
    });
  }

  /**
   * Turns away a Preview or Add to Cart attempt while a pet slot is red:
   * records it, says why, and scrolls to the flag. Returns the red slot, or
   * null when nothing blocks.
   */
  function blockIfRed(action) {
    var config = readConfig();
    if (!isEnabled(config)) return null;
    var red = firstRedSlot();
    if (red == null) return null;
    track(EVENTS.blocked, { slot: red, action: action });
    toast(config.redCartToast);
    scrollToSlot(red);
    return red;
  }

  /** Whether any active pet slot is red right now (no side effects). */
  function hasRed() {
    return isEnabled() && firstRedSlot() != null;
  }

  /** Whether any active pet photo is still being checked (no side effects). */
  function isChecking() {
    return isEnabled() && firstPendingSlot() != null;
  }

  /**
   * Add to Cart gate. Resolves to the slot that blocks the cart - a red one,
   * or one whose check is still running after the cart wait - or null when the
   * cart may go ahead. A photo is never bought unverified; a check that fails
   * or times out is no longer running, so it still fails open.
   */
  function checkoutGate(options) {
    var opts = options || {};
    var config = readConfig();
    if (!isEnabled(config)) return Promise.resolve(null);
    var waiting = hasPending();
    if (waiting && typeof opts.onWait === 'function') opts.onWait(config.cartWaitingText);
    return (waiting ? settleAll(config.cartWaitMs) : Promise.resolve()).then(function () {
      if (waiting && typeof opts.onWaitEnd === 'function') opts.onWaitEnd();
      var red = blockIfRed('add_to_cart');
      if (red != null) return red;
      var checking = firstPendingSlot();
      if (checking != null) {
        track(EVENTS.blocked, { slot: checking, action: 'add_to_cart', reason: 'pending' });
        toast(config.pendingCartToast);
        scrollToSlot(checking);
        return checking;
      }
      activeSlotNumbers().forEach(function (number) {
        var state = slotState(number);
        stamp(number);
        if (state.band === 'yellow') {
          track(EVENTS.proceeded, { slot: number, score: state.score });
        }
      });
      return null;
    });
  }

  /** A read-only view of one slot's result, for the cart and for debugging. */
  function stateOf(number) {
    var state = slotState(number);
    return {
      pending: state.pending,
      band: state.band,
      flag: state.flag,
      score: state.score,
      checkId: state.checkId,
      everFlagged: state.everFlagged,
      recovering: state.recovering,
      source: state.source,
    };
  }

  function previewHint() {
    return readConfig().redPreviewHint;
  }

  return {
    CONFIG_ID: CONFIG_ID,
    FLAG_CHANGE_EVENT: FLAG_CHANGE_EVENT,
    DEFAULTS: DEFAULTS,
    EVENTS: EVENTS,
    parseConfig: parseConfig,
    readConfig: readConfig,
    isEnabled: isEnabled,
    bandFor: bandFor,
    flagFor: flagFor,
    sourceRef: sourceRef,
    createClient: createClient,
    start: start,
    waitBriefly: waitBriefly,
    clear: clear,
    cancel: cancel,
    flagPpiReject: flagPpiReject,
    releasePpiReject: releasePpiReject,
    render: render,
    isRed: isRed,
    stateOf: stateOf,
    firstRedSlot: firstRedSlot,
    hasPending: hasPending,
    hasRed: hasRed,
    isChecking: isChecking,
    blockIfRed: blockIfRed,
    checkoutGate: checkoutGate,
    previewHint: previewHint,
  };
});
